import type { UnlockConfiguration, FeatureType } from '../types';

export const UNLOCK_MODE_SEQUENTIAL = 'sequential' as const;
export const UNLOCK_MODE_CHOICE = 'choice' as const;

export const DOMAIN_UNLOCK_ORDER = [
  'personal',
  'social',
  'occupational',
  'intellectual',
  'avocational',
  'ecological',
  'existential'
] as const;

export interface CapacityFormula {
  featureType: FeatureType;
  baseCapacity: number;
  scalingFactor: number;
  unlockLevel: number;
  description: string;
}

export const CAPACITY_FORMULAS: Record<FeatureType, CapacityFormula> = {
  habits: {
    featureType: 'habits',
    baseCapacity: 3,
    scalingFactor: 0.5,
    unlockLevel: 0,
    description: 'Habits capacity: 3 + (domain_level × 0.5)'
  },
  tasks: {
    featureType: 'tasks',
    baseCapacity: 10,
    scalingFactor: 2,
    unlockLevel: 0,
    description: 'Tasks capacity: 10 + (domain_level × 2)'
  },
  projects: {
    featureType: 'projects',
    baseCapacity: 1,
    scalingFactor: 0.3,
    unlockLevel: 10,
    description: 'Projects capacity: 1 + (domain_level × 0.3) [unlocks at domain level 10]'
  },
  routines: {
    featureType: 'routines',
    baseCapacity: 1,
    scalingFactor: 0.2,
    unlockLevel: 15,
    description: 'Routines capacity: 1 + (domain_level × 0.2) [unlocks at domain level 15]'
  },
  templates: {
    featureType: 'templates',
    baseCapacity: 1,
    scalingFactor: 0.1,
    unlockLevel: 0,
    description: 'Day templates capacity: 1 + (user_level × 0.1)'
  }
};

export function calculateCapacity(
  featureType: FeatureType,
  level: number,
  bonusCapacity: number = 0
): number {
  const formula = CAPACITY_FORMULAS[featureType];

  if (level < formula.unlockLevel) {
    return 0;
  }

  const scaledCapacity = formula.baseCapacity + Math.floor(level * formula.scalingFactor);
  return scaledCapacity + bonusCapacity;
}

export const DEFAULT_UNLOCK_CONFIGURATIONS: Partial<UnlockConfiguration>[] = [
  {
    unlockKey: 'domain_personal',
    unlockType: 'domain',
    unlockName: 'Personal Domain',
    unlockDescription: 'Unlocked by default. Focus on health, fitness, and wellbeing.',
    requirements: {},
    rewards: { domainId: 'personal' },
    displayOrder: 1,
    icon: 'User',
    color: '#ef4444'
  },
  {
    unlockKey: 'domain_social',
    unlockType: 'domain',
    unlockName: 'Social Domain',
    unlockDescription: 'Unlock by completing the first Personal domain quest. Focus on relationships and community.',
    requirements: { questIds: ['personal-quest-0'] },
    rewards: { domainId: 'social' },
    displayOrder: 2,
    icon: 'Users',
    color: '#f97316'
  },
  {
    unlockKey: 'domain_occupational',
    unlockType: 'domain',
    unlockName: 'Occupational Domain',
    unlockDescription: 'Unlock through domain progression. Focus on career and professional growth.',
    requirements: { questIds: ['social-quest-0'] },
    rewards: { domainId: 'occupational' },
    displayOrder: 3,
    icon: 'Briefcase',
    color: '#eab308'
  },
  {
    unlockKey: 'domain_intellectual',
    unlockType: 'domain',
    unlockName: 'Intellectual Domain',
    unlockDescription: 'Unlock through domain progression. Focus on learning and mental growth.',
    requirements: { questIds: ['occupational-quest-0'] },
    rewards: { domainId: 'intellectual' },
    displayOrder: 4,
    icon: 'Brain',
    color: '#a855f7'
  },
  {
    unlockKey: 'domain_avocational',
    unlockType: 'domain',
    unlockName: 'Avocational Domain',
    unlockDescription: 'Unlock through domain progression. Focus on hobbies and leisure.',
    requirements: { questIds: ['intellectual-quest-0'] },
    rewards: { domainId: 'avocational' },
    displayOrder: 5,
    icon: 'Gamepad',
    color: '#22c55e'
  },
  {
    unlockKey: 'domain_ecological',
    unlockType: 'domain',
    unlockName: 'Material Domain',
    unlockDescription: 'Unlock through domain progression. Focus on environment, resources, and household management.',
    requirements: { questIds: ['avocational-quest-0'] },
    rewards: { domainId: 'ecological' },
    displayOrder: 6,
    icon: 'Leaf',
    color: '#3b82f6'
  },
  {
    unlockKey: 'domain_existential',
    unlockType: 'domain',
    unlockName: 'Existential Domain',
    unlockDescription: 'Unlock through domain progression. Focus on spirituality and philosophy.',
    requirements: { questIds: ['ecological-quest-0'] },
    rewards: { domainId: 'existential' },
    displayOrder: 7,
    icon: 'Sparkles',
    color: '#ec4899'
  },
  {
    unlockKey: 'feature_projects',
    unlockType: 'feature',
    unlockName: 'Projects',
    unlockDescription: 'Unlock complex project management with phases, milestones, and tasks.',
    requirements: { domainLevel: { domainId: 'personal', level: 10 } },
    rewards: { featureKey: 'projects' },
    displayOrder: 10,
    icon: 'FolderKanban',
    color: '#3b82f6'
  },
  {
    unlockKey: 'feature_routines',
    unlockType: 'feature',
    unlockName: 'Routines',
    unlockDescription: 'Unlock routine sequences with timed habit flows.',
    requirements: { questIds: ['occupational-quest-0'] },
    rewards: { featureKey: 'routines' },
    displayOrder: 11,
    icon: 'Repeat',
    color: '#22c55e'
  },
  {
    unlockKey: 'feature_abilities',
    unlockType: 'feature',
    unlockName: 'Abilities',
    unlockDescription: 'Unlock the ability skill tree system for structured learning.',
    requirements: { domainLevel: { domainId: 'personal', level: 20 } },
    rewards: { featureKey: 'abilities' },
    displayOrder: 12,
    icon: 'Zap',
    color: '#a855f7'
  },
  {
    unlockKey: 'feature_breathwork_basic',
    unlockType: 'breathwork_style',
    unlockName: 'Breathwork Basics',
    unlockDescription: 'Unlock Stone and Water breathing styles.',
    requirements: { userLevel: 10 },
    rewards: { breathworkStyleId: 'stone,water' },
    displayOrder: 20,
    icon: '🪨',
    color: '#6b7280'
  },
  {
    unlockKey: 'feature_breathwork_all',
    unlockType: 'breathwork_style',
    unlockName: 'All Breathwork Styles',
    unlockDescription: 'Unlock all 7 breathing styles for mastery training.',
    requirements: { userLevel: 20 },
    rewards: { breathworkStyleId: 'flame,wind,thunder,moon,sun' },
    displayOrder: 21,
    icon: '☀️',
    color: '#f59e0b'
  },
  {
    unlockKey: 'feature_social',
    unlockType: 'feature',
    unlockName: 'Social Features',
    unlockDescription: 'Unlock social posts, rooms, and community sharing.',
    requirements: { userLevel: 30 },
    rewards: { featureKey: 'social' },
    displayOrder: 30,
    icon: 'MessageSquare',
    color: '#f97316'
  },
  {
    unlockKey: 'feature_marketplace',
    unlockType: 'feature',
    unlockName: 'Marketplace',
    unlockDescription: 'Unlock the marketplace to browse and share templates, routines, and abilities.',
    requirements: { userLevel: 40 },
    rewards: { featureKey: 'marketplace' },
    displayOrder: 31,
    icon: 'Store',
    color: '#22c55e'
  },
  // Disciplines (DS3) — unlock as the Personal domain levels. Levels MIRROR
  // src/constants/disciplines.ts (the registry is the behavioral source; these
  // supply the celebration card). This is the local/empty-DB fallback; the
  // seeded prod table gets the same rows via the discipline-unlocks migration.
  // NOTE: Nourishment + Rest keep their pre-restructure keys
  // (`discipline_personal_nutrition` / `_sleep`) so users who already crossed
  // their levels don't re-celebrate — only the card content + rewards.disciplineId
  // were remapped to the merged areas. Supplements/Recovery rows were dropped
  // (merged away); Mind/Emotions rows are new. See migration 0203.
  {
    unlockKey: 'discipline_personal_nutrition',
    unlockType: 'discipline',
    unlockName: 'Nourishment',
    unlockDescription: 'Unlocks at Personal level 1. Food, water, and supplements — everything you take in.',
    requirements: { domainLevel: { domainId: 'personal', level: 1 } },
    rewards: { disciplineId: 'nourishment', domainId: 'personal' },
    displayOrder: 40,
    icon: 'Apple',
    color: '#10b981'
  },
  {
    unlockKey: 'discipline_personal_health',
    unlockType: 'discipline',
    unlockName: 'Health',
    unlockDescription: 'Unlocks at Personal level 3. Monitor vitals, symptoms, and wellbeing.',
    requirements: { domainLevel: { domainId: 'personal', level: 3 } },
    rewards: { disciplineId: 'health', domainId: 'personal' },
    displayOrder: 41,
    icon: 'Heart',
    color: '#ef4444'
  },
  {
    // The `emotions` area became **Grooming**; the KEY stays legacy so users keep
    // the unlock they earned (as `nourishment`/`rest` do). Mirror of the row
    // migration 0209 relabels — keep the two in step.
    unlockKey: 'discipline_personal_emotions',
    unlockType: 'discipline',
    unlockName: 'Grooming',
    unlockDescription: 'Unlocks at Personal level 4. Cleanliness, appearance, scent and presentation — your daily care system.',
    requirements: { domainLevel: { domainId: 'personal', level: 4 } },
    rewards: { disciplineId: 'grooming', domainId: 'personal' },
    displayOrder: 42,
    icon: 'Sparkles',
    color: '#14b8a6'
  },
  {
    unlockKey: 'discipline_personal_fitness',
    unlockType: 'discipline',
    unlockName: 'Fitness',
    unlockDescription: 'Unlocks at Personal level 5. Level performance parameters and muscle groups.',
    requirements: { domainLevel: { domainId: 'personal', level: 5 } },
    rewards: { disciplineId: 'fitness', domainId: 'personal' },
    displayOrder: 43,
    icon: 'Dumbbell',
    color: '#f97316'
  },
  {
    unlockKey: 'discipline_personal_sleep',
    unlockType: 'discipline',
    unlockName: 'Rest',
    unlockDescription: 'Unlocks at Personal level 8. Sleep and recovery — how you restore.',
    requirements: { domainLevel: { domainId: 'personal', level: 8 } },
    rewards: { disciplineId: 'rest', domainId: 'personal' },
    displayOrder: 44,
    icon: 'Moon',
    color: '#6366f1'
  },
  {
    unlockKey: 'discipline_personal_mind',
    unlockType: 'discipline',
    unlockName: 'Mind',
    unlockDescription: 'Unlocks at Personal level 10. Mental & emotional care.',
    requirements: { domainLevel: { domainId: 'personal', level: 10 } },
    rewards: { disciplineId: 'mind', domainId: 'personal' },
    displayOrder: 45,
    icon: 'Brain',
    color: '#8b5cf6'
  },
  {
    unlockKey: 'discipline_personal_breathing',
    unlockType: 'discipline',
    unlockName: 'Coherence',
    unlockDescription: 'Unlocks at Personal level 18. Train your breath coherence and run breathwork sessions.',
    requirements: { domainLevel: { domainId: 'personal', level: 18 } },
    rewards: { disciplineId: 'breathing', domainId: 'personal' },
    displayOrder: 46,
    icon: 'Wind',
    color: '#0ea5e9'
  }
];

export const QUEST_TEMPLATES = {
  'personal-quest-0': {
    title: 'Foundation Builder',
    description: 'Learn the basics of METANOIA by completing your first tasks and habits in the Personal domain.',
    category: 'personal',
    goals: [
      { actionVerb: 'Complete', what: 'tasks', quantity: 5 },
      { actionVerb: 'Complete', what: 'daily habits', quantity: 3 }
    ],
    rewards: ['Unlock Social Domain', 'Increase Personal capacity']
  },
  'personal-quest-10': {
    title: 'Personal Mastery',
    description: 'Demonstrate consistent personal development.',
    category: 'personal',
    goals: [
      { actionVerb: 'Complete', what: 'tasks', quantity: 25 },
      { actionVerb: 'Maintain', what: 'habit streak', quantity: 7 }
    ],
    rewards: ['Unlock Projects feature', 'Increase template capacity']
  },
  'social-quest-0': {
    title: 'Social Connection',
    description: 'Begin building your social and relationship goals.',
    category: 'social',
    goals: [
      { actionVerb: 'Complete', what: 'social tasks', quantity: 5 },
      { actionVerb: 'Create', what: 'social habits', quantity: 2 }
    ],
    rewards: ['Unlock Occupational Domain', 'Unlock Routines feature']
  },
  'user-quest-10': {
    title: 'Rising Star',
    description: 'Reach user level 10 and unlock breathwork training.',
    category: 'personal',
    goals: [
      { actionVerb: 'Reach', what: 'user level', quantity: 10 },
      { actionVerb: 'Complete', what: 'domain quests', quantity: 2 }
    ],
    rewards: ['Unlock Breathwork (Stone & Water styles)']
  },
  'user-quest-20': {
    title: 'Balanced Growth',
    description: 'Achieve balanced progress across multiple domains.',
    category: 'personal',
    goals: [
      { actionVerb: 'Reach', what: 'level 10 in', quantity: 2 },
      { actionVerb: 'Complete', what: 'breathwork sessions', quantity: 10 }
    ],
    rewards: ['Unlock all Breathwork styles']
  }
};
